/**
 * Named radii, in pixels.
 *
 * Professional ("Structured" roundness) defines four roles: controls/inputs
 * 6, cards 8, dialogs 10, and buttons/pills/chips 999. The names below
 * predate that — `sm`/`md`/`lg`/`xl`/`xxl` were recorded during the
 * decoupling wave and encoded the magnitudes the app happened to render
 * then (5/10/15/20/25).
 *
 * The Professional flip maps each onto the nearest Professional role rather
 * than inventing a fifth scalar value, which is why several names now share
 * a number:
 *
 *   none  0  → 0            unchanged
 *   sm    8  → 6   control  the small radius is Professional's input radius
 *   md   10  → 8   card     Professional's card radius
 *   lg   12  → 10  dialog   Professional's largest scalar radius
 *   xl   12  → 10  dialog   Professional has nothing between 10 and 999
 *   xxl  12  → 10  dialog   same
 *
 * The t-shirt names are now misleading — `lg`, `xl` and `xxl` are one value.
 * Renaming them to their Professional roles means touching ~130 call sites, so
 * it is deliberately left for a follow-up: this file is the one place the
 * numbers live, and correcting the numbers matters more than correcting the
 * labels.
 *
 * Numeric entries are px magnitudes — a call site wraps them in a template
 * (`` `${radii.lg}px` ``) to get an explicit-unit string, EXCEPT `none`: a
 * bare `borderRadius: 0` and a templated `borderRadius: "0px"` serialize to
 * different emotion classes, so every zero call site uses `radii.none` bare.
 *
 * String entries (`circle` and below) are already complete CSS values, used
 * bare and never templated. The multi-corner strings are shape decorations
 * rather than scalar radii and are unaffected by roundness.
 */
const radii = {
  none: 0,
  /**
   * Was Playful's `chip` role at 9. Professional draws chips as pills, so
   * every one of this key's five non-chip call sites (the auth error
   * banner, the AI-critique panels) wants `control`, and the chip itself
   * wants `pill`. The name is wrong either way — Wave B deletes it.
   */
  chip: 999,
  /** Professional `control` — inputs, menu rows, alerts. */
  sm: 6,
  /** Professional `card`. */
  md: 8,
  /** Professional `dialog`. */
  lg: 10,
  /** Toast severity `Paper`, invite `Chip` border, header menu. `dialog`. */
  xl: 10,
  /** Hub engagement `Paper`, project-role `Chip`, note `Card`s. `dialog`. */
  xxl: 10,
  /** `.btn-main` primary submit button. Professional buttons are pills. */
  submitButton: 999,
  /** Floating panel containers — `modalCss.ts`, `RebelMap`. `card`. */
  panel: 8,
  /** `Pages/NoMatch` scrim box. `card`. */
  noMatchScrim: 8,
  circle: "50%",
  /** Raised `Card`s: member grid card, activity/question reply cards, activity-tile skeleton. */
  raisedCard: "8px",
  /** `HubMember` `.member-card__logo`. */
  hubMemberCardLogo: "5%",
  /** `Members` `.member-card__profile-img`. */
  memberCardAvatar: "100%",
  /** `Members` `.member-card__summary` base wedge. */
  memberCardSummaryBase: "50% 0 0 0",
  /** `Members` `.member-card__summary` — rebel variant. */
  memberCardSummaryRebel: "35% 0 0 0",
  /** `Members` `.member-card__summary` — admin variant. */
  memberCardSummaryAdmin: "15% 0 0 0",
  /** `Members` `.member-card__summary` — judge variant. */
  memberCardSummaryJudge: "55% 0 0 0",
  /** `Members` `.member-card__summary` — partner and general variants (shared). */
  memberCardSummaryPartner: "30% 0 0 0",
  /** `HubMember` `.member-card__summary` base wedge. */
  hubMemberCardSummaryBase: "25% 0 0 0",
} as const;

export default radii;
